// Chunk mesher (ENG-12/15). PURE (ENG-1): emits plain typed-array geometry the
// client uploads into a THREE.BufferGeometry — no three/DOM here, so the same
// code runs in the mesher worker (M1.3) and in tests. Ported from the legacy
// `buildMesh`: culled faces merged per chunk, per-vertex AO × directional shade.
// Per-vertex sky/block light (0..1) is sampled from the adjacent voxels (§5.1)
// and emitted as two attributes; the shader combines them with the daylight
// uniform (LIT-5), so dusk needs no remesh.
import { WORLD, LIGHT } from '../config';
import { STATE_AIR } from '../engine/section';
import { columnIndex } from '../engine/heightmap';
import type { Chunk } from '../engine/chunk';
import { CUBE_FACES } from './shapes/cube';
import { faceUV, LAYER_CUTOUT, LAYER_TRANSLUCENT, type RenderTable } from './atlasmap';
import { SHAPE, blockBoxes, type Box } from '../shapes';

/** Torch geometry: a thin post, 10/16 tall (ENG-13). */
const TORCH_BOX: Box = [7 / 16, 0, 7 / 16, 9 / 16, 10 / 16, 9 / 16];

const LIGHT_MAX = LIGHT.MAX;

export interface GeoBuffers {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  colors: Float32Array;
  /** Per-vertex sky-light level, normalized 0..1 (scaled by daylight in-shader). */
  sky: Float32Array;
  /** Per-vertex block-light level, normalized 0..1 (daylight-independent). */
  block: Float32Array;
  indices: Uint32Array;
}

export interface ChunkMesh {
  /** World-space translation for the whole chunk mesh (chunk origin). */
  originX: number;
  originZ: number;
  opaque: GeoBuffers | null;
  cutout: GeoBuffers | null;
  translucent: GeoBuffers | null;
}

/** A read-only block + light accessor in world coordinates (World satisfies it). */
export interface BlockView {
  getBlockState(x: number, y: number, z: number): number;
  getSkyLight(x: number, y: number, z: number): number;
  getBlockLight(x: number, y: number, z: number): number;
}

interface Accum {
  positions: number[];
  normals: number[];
  uvs: number[];
  colors: number[];
  sky: number[];
  block: number[];
  indices: number[];
  vertCount: number;
}

const newAccum = (): Accum => ({ positions: [], normals: [], uvs: [], colors: [], sky: [], block: [], indices: [], vertCount: 0 });

function finish(a: Accum): GeoBuffers | null {
  if (a.indices.length === 0) return null;
  return {
    positions: new Float32Array(a.positions),
    normals: new Float32Array(a.normals),
    uvs: new Float32Array(a.uvs),
    colors: new Float32Array(a.colors),
    sky: new Float32Array(a.sky),
    block: new Float32Array(a.block),
    indices: new Uint32Array(a.indices),
  };
}

/** Emit one box's exposed faces (slab/stairs/layer/torch). Flat-shaded (no AO);
 *  UV is full-tile per face (proportional UV is M10 polish). Light is sampled
 *  from the cell the face looks into, in the face direction. */
/**
 * Texture UV for a sub-box corner, taken from the box's actual extent along the
 * face's tangent axes (M2.2/M10.2 proportional UV) — so a bottom slab shows the
 * lower half of its texture on the sides, not the whole tile squished. The
 * per-face axis map + flips reproduce the full-cube UVs exactly when the box is
 * the unit cube (bx/by/bz ∈ {0,1}), so cubes are unchanged; only sub-boxes crop.
 */
/** Rotate a unit-square UV by `quarter` 90° turns about its centre (glazed
 *  terracotta's directional top texture, MB.1). */
export function rotateUV(u: number, v: number, quarter: number): [number, number] {
  switch (quarter & 3) {
    case 1: return [v, 1 - u];
    case 2: return [1 - u, 1 - v];
    case 3: return [1 - v, u];
    default: return [u, v];
  }
}

export function boxFaceUV(f: number, bx: number, by: number, bz: number): [number, number] {
  switch (f) {
    case 0: return [bz, by];         // -x : u=z, v=y
    case 1: return [1 - bz, by];     // +x
    case 2: return [bx, 1 - bz];     // -y : u=x, v=z
    case 3: return [1 - bx, bz];     // +y
    case 4: return [1 - bx, by];     // -z : u=x, v=y
    default: return [bx, by];        // +z
  }
}

function emitBox(acc: Accum, lx: number, y: number, lz: number, box: Box, self: number, table: RenderTable, view: BlockView, wx: number, wz: number): void {
  const [x0, y0, z0, x1, y1, z1] = box;
  for (let f = 0; f < 6; f++) {
    const face = CUBE_FACES[f]!;
    const boundary =
      (f === 0 && x0 === 0) || (f === 1 && x1 === 1) ||
      (f === 2 && y0 === 0) || (f === 3 && y1 === 1) ||
      (f === 4 && z0 === 0) || (f === 5 && z1 === 1);
    const fx = wx + face.dir[0], fy = y + face.dir[1], fz = wz + face.dir[2];
    if (boundary && table.opaque[view.getBlockState(fx, fy, fz)] === 1) continue;
    const tile = table.tiles[self * 6 + f]!;
    const light = face.shade;
    const skyL = view.getSkyLight(fx, fy, fz) / LIGHT_MAX;
    const blkL = view.getBlockLight(fx, fy, fz) / LIGHT_MAX;
    const c0 = acc.vertCount;
    for (let v = 0; v < 4; v++) {
      const corner = face.corners[v]!;
      const p = corner.pos;
      const bx = p[0] ? x1 : x0, by = p[1] ? y1 : y0, bz = p[2] ? z1 : z0;
      acc.positions.push(lx + bx, y + by, lz + bz);
      acc.normals.push(face.dir[0], face.dir[1], face.dir[2]);
      const [tu, tv] = boxFaceUV(f, bx, by, bz);
      const uv = faceUV(tile, tu, tv);
      acc.uvs.push(uv[0], uv[1]);
      acc.colors.push(light, light, light);
      acc.sky.push(skyL);
      acc.block.push(blkL);
    }
    acc.indices.push(c0, c0 + 1, c0 + 2, c0 + 2, c0 + 1, c0 + 3);
    acc.vertCount += 4;
  }
}

/** Emit a two-plane "X" cross (tall grass / flowers), double-sided. Lit by the
 *  plant's own cell (plants don't occlude light). */
function emitCross(acc: Accum, lx: number, y: number, lz: number, tile: number, view: BlockView, wx: number, wz: number): void {
  const light = 1;
  const skyL = view.getSkyLight(wx, y, wz) / LIGHT_MAX;
  const blkL = view.getBlockLight(wx, y, wz) / LIGHT_MAX;
  const m = 0.15, M = 0.85;
  const planes: Array<readonly [number, number, number, number]> = [
    [m, m, M, M],
    [m, M, M, m],
  ];
  for (const [ax, az, bx, bz] of planes) {
    for (let side = 0; side < 2; side++) {
      const c0 = acc.vertCount;
      const corners = side === 0
        ? [[ax, 1, az], [ax, 0, az], [bx, 1, bz], [bx, 0, bz]]
        : [[bx, 1, bz], [bx, 0, bz], [ax, 1, az], [ax, 0, az]];
      const uvs = [[0, 1], [0, 0], [1, 1], [1, 0]];
      for (let v = 0; v < 4; v++) {
        acc.positions.push(lx + corners[v]![0]!, y + corners[v]![1]!, lz + corners[v]![2]!);
        acc.normals.push(0, 1, 0);
        const uv = faceUV(tile, uvs[v]![0]!, uvs[v]![1]!);
        acc.uvs.push(uv[0], uv[1]);
        acc.colors.push(light, light, light);
        acc.sky.push(skyL);
        acc.block.push(blkL);
      }
      acc.indices.push(c0, c0 + 1, c0 + 2, c0 + 2, c0 + 1, c0 + 3);
      acc.vertCount += 4;
    }
  }
}

/** Emit a nether-portal plane (DIM-2): a thin double-sided quad in the block's
 *  centre, oriented by its `axis` property. Lit by its own (emissive) cell. */
function emitPortal(acc: Accum, lx: number, y: number, lz: number, tile: number, props: Readonly<Record<string, string>>, view: BlockView, wx: number, wz: number): void {
  const light = 1;
  const skyL = view.getSkyLight(wx, y, wz) / LIGHT_MAX;
  const blkL = view.getBlockLight(wx, y, wz) / LIGHT_MAX;
  const axisX = (props.axis ?? 'x') === 'x';
  const c = 0.5; // plane sits in the block centre
  // Corners of the plane (bottom-left, top-left, bottom-right, top-right in local space).
  const quad = axisX
    ? [[0.02, 0, c], [0.02, 1, c], [0.98, 0, c], [0.98, 1, c]]
    : [[c, 0, 0.02], [c, 1, 0.02], [c, 0, 0.98], [c, 1, 0.98]];
  const uvs = [[0, 0], [0, 1], [1, 0], [1, 1]];
  for (let side = 0; side < 2; side++) {
    const order = side === 0 ? [0, 1, 2, 3] : [2, 3, 0, 1];
    const c0 = acc.vertCount;
    for (let v = 0; v < 4; v++) {
      const idx = order[v]!;
      const p = quad[idx]!;
      acc.positions.push(lx + p[0]!, y + p[1]!, lz + p[2]!);
      acc.normals.push(axisX ? 0 : side === 0 ? 1 : -1, 0, axisX ? (side === 0 ? 1 : -1) : 0);
      const uv = faceUV(tile, uvs[idx]![0]!, uvs[idx]![1]!);
      acc.uvs.push(uv[0], uv[1]);
      acc.colors.push(light, light, light);
      acc.sky.push(skyL);
      acc.block.push(blkL);
    }
    acc.indices.push(c0, c0 + 1, c0 + 2, c0 + 2, c0 + 1, c0 + 3);
    acc.vertCount += 4;
  }
}

/** Surface height (0..1) of a fluid cell: full when a same fluid is directly
 *  above (a continuous column), 14/16 for a source, tapering with flow level. */
function fluidHeight(level: number, above: boolean): number {
  if (above) return 1;
  return level === 0 ? 0.88 : (8 - level) / 9;
}

/** Emit a flowing-fluid cell (ENG-14): a lowered top surface by `level`, culled
 *  against opaque neighbours and merged against taller same-fluid neighbours so
 *  a waterfall reads as flowing rather than a wall of cubes. */
function emitFluid(acc: Accum, lx: number, y: number, lz: number, self: number, table: RenderTable, view: BlockView, wx: number, wz: number): void {
  const selfBlock = table.blockOf[self];
  const isWaterSelf = selfBlock === table.waterBlock;
  const isOpaque = (st: number): boolean => table.opaque[st] === 1;
  // Water is continuous through a waterlogged neighbour (source-level), so a face
  // shared with one is culled just like a face shared with a plain water cell.
  const sameFluid = (st: number): boolean =>
    (table.blockOf[st] === selfBlock && table.shape[st] === SHAPE.FLUID) ||
    (isWaterSelf && table.waterlogged[st] === 1);
  const levelOf = (st: number): number =>
    table.waterlogged[st] === 1 ? 0 : Number(table.props[st]?.level ?? '0');
  const level = levelOf(self);
  const above = sameFluid(view.getBlockState(wx, y + 1, wz));
  const h = fluidHeight(level, above);
  const neighbourHeight = (st: number, sx: number, sy: number, sz: number): number =>
    sameFluid(st) ? fluidHeight(levelOf(st), sameFluid(view.getBlockState(sx, sy + 1, sz))) : -1;

  for (let f = 0; f < 6; f++) {
    const face = CUBE_FACES[f]!;
    const nx = wx + face.dir[0], ny = y + face.dir[1], nz = wz + face.dir[2];
    const nSt = view.getBlockState(nx, ny, nz);
    if (isOpaque(nSt)) continue;
    let yLo = 0;
    if (f === 2 || f === 3) {
      if (sameFluid(nSt)) continue; // top/bottom hidden by an adjacent fluid cell
    } else {
      const nh = neighbourHeight(nSt, nx, ny, nz);
      if (nh >= 0) {
        if (nh >= h) continue; // neighbour as tall or taller — no visible side
        yLo = nh; // only the exposed strip above the shorter neighbour
      }
    }
    const tile = table.tiles[self * 6 + f]!;
    const shade = face.shade;
    const skyL = view.getSkyLight(nx, ny, nz) / LIGHT_MAX;
    const blkL = view.getBlockLight(nx, ny, nz) / LIGHT_MAX;
    const c0 = acc.vertCount;
    for (let v = 0; v < 4; v++) {
      const corner = face.corners[v]!;
      const p = corner.pos;
      const py = f === 3 ? h : p[1] === 1 ? h : yLo;
      acc.positions.push(lx + p[0], y + py, lz + p[2]);
      acc.normals.push(face.dir[0], face.dir[1], face.dir[2]);
      const uvv = f === 2 || f === 3 ? corner.uv[1] : p[1] === 1 ? h : yLo;
      const uv = faceUV(tile, corner.uv[0], uvv);
      acc.uvs.push(uv[0], uv[1]);
      acc.colors.push(shade, shade, shade);
      acc.sky.push(skyL);
      acc.block.push(blkL);
    }
    acc.indices.push(c0, c0 + 1, c0 + 2, c0 + 2, c0 + 1, c0 + 3);
    acc.vertCount += 4;
  }
}

/** Meshing options (ENG-12). Default (none) = full column, the canonical path. */
export interface MeshOptions {
  /** Surface-only LOD (perf, flight): mesh only a shell from each column's surface
   *  down this many blocks; deeper cells are skipped. From altitude the pilot sees
   *  the top skin, and the shell's underside is still culled against the real
   *  (unmeshed but present) blocks below, so no spurious faces appear. 0/undefined
   *  ⇒ full column — golden-identical to the pre-LOD mesher. */
  lodFloorDepth?: number;
}

export function meshChunk(view: BlockView, chunk: Chunk, table: RenderTable, opts?: MeshOptions): ChunkMesh {
  const bx = chunk.cx * WORLD.SECTION;
  const bz = chunk.cz * WORLD.SECTION;
  // The dimension's own floor doubles as the NO_SURFACE sentinel (TAL-5).
  const noSurface = chunk.range.minY;

  // Top of the tallest column (heightmap already accounts for tree canopies).
  const ws = chunk.heightmaps.worldSurface;
  let top: number = noSurface;
  for (let i = 0; i < ws.length; i++) if (ws[i]! > top) top = ws[i]!;

  const opaqueAcc = newAccum();
  const cutoutAcc = newAccum();
  const translucentAcc = newAccum();
  if (top === noSurface) {
    return { originX: bx, originZ: bz, opaque: null, cutout: null, translucent: null };
  }
  const yMax = Math.min(chunk.range.maxY, top - 1);

  // Surface-only LOD: raise the loop floor to the lowest per-column shell base so
  // the deep subsurface is never even visited (the big win at altitude).
  const lodDepth = opts?.lodFloorDepth ?? 0;
  let yStart: number = chunk.range.minY;
  if (lodDepth > 0) {
    let minFloor = chunk.range.maxY;
    for (let i = 0; i < ws.length; i++) {
      const s = ws[i]!;
      if (s <= noSurface) continue; // all-air column contributes no floor
      if (s - lodDepth < minFloor) minFloor = s - lodDepth;
    }
    yStart = Math.max(chunk.range.minY, minFloor);
  }

  const isOpaque = (state: number): boolean => table.opaque[state] === 1;

  for (let y = yStart; y <= yMax; y++) {
    for (let lz = 0; lz < WORLD.SECTION; lz++) {
      for (let lx = 0; lx < WORLD.SECTION; lx++) {
        // LOD: skip cells more than lodDepth below this column's surface.
        if (lodDepth > 0) { const s = ws[columnIndex(lx, lz)]!; if (s > noSurface && y < s - lodDepth) continue; }
        const self = chunk.getBlockState(lx, y, lz);
        if (self === STATE_AIR || table.hasMesh[self] === 0) continue;
        const wx = bx + lx, wz = bz + lz;
        const selfBlock = table.blockOf[self];
        const selfLayer = table.layer[self];
        const acc = selfLayer === LAYER_TRANSLUCENT ? translucentAcc : selfLayer === LAYER_CUTOUT ? cutoutAcc : opaqueAcc;
        const mergesSelf = selfLayer === LAYER_CUTOUT || selfLayer === LAYER_TRANSLUCENT;
        const shp = table.shape[self]!;

        if (shp === SHAPE.CROSS) {
          emitCross(acc, lx, y, lz, table.tiles[self * 6]!, view, wx, wz);
          continue;
        }
        if (shp === SHAPE.PORTAL) {
          emitPortal(acc, lx, y, lz, table.tiles[self * 6]!, table.props[self]!, view, wx, wz);
          continue;
        }
        if (shp === SHAPE.FLUID) {
          emitFluid(acc, lx, y, lz, self, table, view, wx, wz);
          continue;
        }
        // A waterlogged slab/stairs/fence draws source water around its shape (M9.1).
        if (table.waterlogged[self] === 1 && table.waterState >= 0) {
          emitFluid(translucentAcc, lx, y, lz, table.waterState, table, view, wx, wz);
        }
        if (shp !== SHAPE.CUBE && shp !== SHAPE.FLUID) {
          const boxes = shp === SHAPE.TORCH ? [TORCH_BOX] : blockBoxes(shp, table.props[self]!);
          for (const box of boxes) emitBox(acc, lx, y, lz, box, self, table, view, wx, wz);
          continue;
        }

        for (let f = 0; f < 6; f++) {
          const face = CUBE_FACES[f]!;
          const nx = wx + face.dir[0], ny = y + face.dir[1], nz = wz + face.dir[2];
          const neighbor = view.getBlockState(nx, ny, nz);
          // Cull behind opaque neighbors, and same-block cutout/water against itself.
          if (isOpaque(neighbor)) continue;
          if (mergesSelf && table.blockOf[neighbor] === selfBlock) continue;

          const tile = table.tiles[self * 6 + f]!;
          // Glazed terracotta rotates its top/bottom texture by facing (MB.1); 0
          // for everything else, so all other blocks emit byte-identical UVs.
          const rot = (f === 2 || f === 3) ? table.rotTop[self]! : 0;
          const ao = [0, 0, 0, 0];

          for (let v = 0; v < 4; v++) {
            const corner = face.corners[v]!;
            const p = corner.pos;
            acc.positions.push(lx + p[0], y + p[1], lz + p[2]);
            acc.normals.push(face.dir[0], face.dir[1], face.dir[2]);
            const [ru, rv] = rot === 0 ? [corner.uv[0], corner.uv[1]] : rotateUV(corner.uv[0], corner.uv[1], rot);
            const uv = faceUV(tile, ru, rv);
            acc.uvs.push(uv[0], uv[1]);

            // Per-vertex AO from the 3 cells flanking this corner (legacy).
            const a = face.axis, t1 = (a + 1) % 3, t2 = (a + 2) % 3;
            const base = [nx, ny, nz];
            const o1 = p[t1] === 1 ? 1 : -1;
            const o2 = p[t2] === 1 ? 1 : -1;
            const s1c = [...base]; s1c[t1]! += o1;
            const s2c = [...base]; s2c[t2]! += o2;
            const cc = [...base]; cc[t1]! += o1; cc[t2]! += o2;
            const s1 = isOpaque(view.getBlockState(s1c[0]!, s1c[1]!, s1c[2]!)) ? 1 : 0;
            const s2 = isOpaque(view.getBlockState(s2c[0]!, s2c[1]!, s2c[2]!)) ? 1 : 0;
            const co = isOpaque(view.getBlockState(cc[0]!, cc[1]!, cc[2]!)) ? 1 : 0;
            ao[v] = s1 && s2 ? 0 : 3 - (s1 + s2 + co);

            const light = face.shade * (0.55 + ao[v]! * 0.15);
            acc.colors.push(light, light, light);

            // Smooth per-vertex light (§5.1): average the up-to-4 non-opaque
            // voxels touching this corner on the outside of the face; the front
            // voxel always counts, opaque flankers are skipped (they carry no light).
            let sky = view.getSkyLight(nx, ny, nz), blk = view.getBlockLight(nx, ny, nz), n = 1;
            if (!s1) { sky += view.getSkyLight(s1c[0]!, s1c[1]!, s1c[2]!); blk += view.getBlockLight(s1c[0]!, s1c[1]!, s1c[2]!); n++; }
            if (!s2) { sky += view.getSkyLight(s2c[0]!, s2c[1]!, s2c[2]!); blk += view.getBlockLight(s2c[0]!, s2c[1]!, s2c[2]!); n++; }
            if (!co) { sky += view.getSkyLight(cc[0]!, cc[1]!, cc[2]!); blk += view.getBlockLight(cc[0]!, cc[1]!, cc[2]!); n++; }
            acc.sky.push(sky / n / LIGHT_MAX);
            acc.block.push(blk / n / LIGHT_MAX);
          }

          const c0 = acc.vertCount;
          if (ao[0]! + ao[3]! > ao[1]! + ao[2]!) {
            acc.indices.push(c0 + 1, c0 + 3, c0, c0 + 3, c0 + 2, c0);
          } else {
            acc.indices.push(c0, c0 + 1, c0 + 2, c0 + 2, c0 + 1, c0 + 3);
          }
          acc.vertCount += 4;
        }
      }
    }
  }

  return {
    originX: bx,
    originZ: bz,
    opaque: finish(opaqueAcc),
    cutout: finish(cutoutAcc),
    translucent: finish(translucentAcc),
  };
}
